import { ReactNode, useState } from "react";
import { Confirm, ConfirmationDialogContext, ConfirmationDialogState } from "./ConfirmationDialogContext";
import ConfirmationDialog from "components/ConfirmationDialog";
import { UsePopover } from "helpers/hooks/usePopover";
import { ButtonProps } from "@mui/material";

const NoOp = async () => {
  return Promise.resolve();
};

export function ConfirmationDialogProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [title, setTitle] = useState("");
  const [prompt, setPrompt] = useState<ReactNode | undefined>(undefined);
  const [callback, setCallback] = useState<() => Promise<void>>(NoOp);
  const [usePopover, setUsePopover] = useState<UsePopover | undefined>(undefined);
  const [confirmButtonProps, setConfirmButtonProps] = useState<ButtonProps>({});
  const [cancelButtonProps, setCancelButtonProps] = useState<ButtonProps>({});
  const [confirmButtonText, setConfirmButtonText] = useState<string | undefined>(undefined);
  const show = open || usePopover?.isOpen;

  const closeConfirm = () => {
    setOpen(false);
    setUsePopover(undefined);
  };

  const confirm = (c: Confirm): void => {
    setOpen(true);
    setTitle(c.title);
    setPrompt(c.prompt);
    setCallback(() => c.callback);
    setConfirmButtonProps(c.confirmButtonProps ?? {});
    setConfirmButtonText(c.confirmButtonText);
    setCancelButtonProps(c.cancelButtonProps ?? {});
    setUsePopover(c.usePopover);
  };

  const onCancel = () => {
    closeConfirm();
  };
  const onConfirm = async () => {
    try {
      setConfirming(true);
      await callback();
    } catch (e) {
      console.error(e);
    } finally {
      closeConfirm();
      setConfirming(false);
    }
  };

  const cdSt: ConfirmationDialogState = {
    open,
    title,
    confirming,
    prompt,
    callback,
    usePopover,
    confirmButtonProps,
    confirmButtonText,
    cancelButtonProps,
    confirm,
    onCancel,
    onConfirm,
  };

  return (
    <ConfirmationDialogContext.Provider value={cdSt}>
      {show && <ConfirmationDialog {...cdSt} />}
      {children}
    </ConfirmationDialogContext.Provider>
  );
}
